import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, FileText, Download, CreditCard, Building2, Loader2, BadgePercent, Upload, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import StatusTracker from "@/components/StatusTracker";
import Brand from "@/components/Brand";
import { api, API } from "@/lib/api";
import { trackEvent } from "@/lib/gtag";
import { formatUSD } from "@/lib/constants";
import DocPreviewModal from "@/pages/DocPreviewModal";
import { useTranslation, Trans } from "react-i18next";

const STATUS_LABEL = {
  pending_payment: { text: "portal.statDue", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  in_progress: { text: "portal.statusInProgress", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  completed: { text: "portal.statusCompleted", cls: "bg-orange-100 text-orange-700 border-orange-200" },
};

const DOC_STATUS = {
  required: { t: "client.dsRequired", c: "bg-amber-100 text-amber-700 border-amber-200" },
  uploaded: { t: "client.dsUploaded", c: "bg-blue-100 text-blue-700 border-blue-200" },
  under_review: { t: "client.dsUnderReview", c: "bg-blue-100 text-blue-700 border-blue-200" },
  approved: { t: "client.dsApproved", c: "bg-green-100 text-green-700 border-green-200" },
  rejected: { t: "client.dsRejected", c: "bg-red-100 text-red-700 border-red-200" },
  action_required: { t: "client.dsActionRequired", c: "bg-orange-100 text-orange-700 border-orange-200" },
};
const needsAction = (s) => s === "required" || s === "rejected" || s === "action_required";

export default function RequestDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);
  const targetDoc = useRef(null);

  useEffect(() => {
    api.get(`/requests/${id}`).then((r) => setReq(r.data)).catch(() => toast.error(t("client.reqNotFoundToast"))).finally(() => setLoading(false));
  }, [id]);

  const pay = async () => {
    setPaying(true);
    try {
      const res = await api.post("/payments/checkout", { package_id: req.package_id, request_id: req.id, origin_url: window.location.origin });
      trackEvent("begin_checkout", { package_name: req.package_name, amount: req.amount, currency: req.currency || "usd" });
      try { localStorage.setItem("uni_pending_purchase", JSON.stringify({ session_id: res.data.session_id, amount: req.amount, currency: req.currency || "usd" })); } catch { /* noop */ }
      window.location.href = res.data.url;
    } catch { toast.error(t("client.checkoutError")); setPaying(false); }
  };

  const pickFile = (docId) => { targetDoc.current = docId || null; fileRef.current?.click(); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const docId = targetDoc.current;
    setUploading(docId || "new");
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (docId) fd.append("doc_id", docId);
      const res = await api.post(`/requests/${id}/documents`, fd);
      setReq((r) => ({ ...r, req_documents: res.data.req_documents }));
      toast.success(t("client.docUploaded"));
    } catch { toast.error(t("client.uploadFailedRetry")); } finally { setUploading(null); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-orange-700" /></div>;

  if (!req) return (
    <div className="min-h-screen grid place-items-center text-center px-6">
      <div><p className="font-heading text-2xl text-foreground">{t("client.reqNotFound")}</p><Button className="mt-5 rounded-sm" onClick={() => navigate("/dashboard")}>{t("client.backDashboard")}</Button></div>
    </div>
  );

  const s = STATUS_LABEL[req.status] || STATUS_LABEL.pending_payment;
  const saving = (req.line_items || []).filter((li) => li.amount < 0).reduce((a, li) => a + Math.abs(li.amount), 0);
  const reqDocs = req.req_documents || [];
  const actionCount = reqDocs.filter((d) => needsAction(d.status)).length;

  return (
    <div className="min-h-screen grain">
      <input type="file" ref={fileRef} onChange={onFile} className="hidden" data-testid="doc-file-input" />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="detail-back"><ArrowLeft className="h-4 w-4" /> {t("client.dashboard")}</button>
          <Brand size="sm" to="/dashboard" />
        </div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-8" data-testid="request-detail">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-orange-700">{req.category}</p>
              <h1 className="mt-2 font-heading text-4xl tracking-tight text-foreground">{req.package_name}</h1>
              <p className="mt-1.5 text-muted-foreground">{req.company_name}{req.jurisdiction ? ` · ${req.jurisdiction}` : ""}</p>
            </div>
            <div className="flex items-center gap-2">
              {req.category === "Bundle" && <Badge className="rounded-sm border border-orange-200 bg-orange-100 text-orange-700 hover:bg-orange-100 text-xs uppercase tracking-wider">{t("client.bundle")}</Badge>}
              <Badge className={`rounded-sm border text-xs uppercase tracking-wider ${s.cls}`}>{t(s.text)}</Badge>
            </div>
          </div>

          {saving > 0 && (
            <div className="mt-6 rounded-xl border border-green-200 bg-green-50 p-4 flex items-center gap-3 card-soft" data-testid="detail-bundle-saving">
              <span className="grid place-items-center h-9 w-9 rounded-sm bg-green-100 text-green-700 shrink-0"><BadgePercent className="h-5 w-5" /></span>
              <p className="text-sm text-green-800"><Trans i18nKey="client.bundleSaving" values={{ amount: formatUSD(saving) }} components={{ b: <span className="font-semibold" /> }} /></p>
            </div>
          )}

          {actionCount > 0 && (
            <div className="mt-6 rounded-xl border border-orange-200 bg-orange-50 p-4 flex items-center gap-3 card-soft" data-testid="detail-doc-alert">
              <AlertCircle className="h-5 w-5 text-orange-600 shrink-0" />
              <p className="text-sm text-orange-800">{actionCount > 1 ? t("client.docsAttentionMany", { count: actionCount }) : t("client.docsAttentionOne")}</p>
            </div>
          )}

          {req.payment_status !== "paid" && (
            <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-5 flex items-center justify-between gap-4 flex-wrap card-soft" data-testid="detail-pay-banner">
              <div className="flex items-center gap-3">
                <CreditCard className="h-5 w-5 text-amber-600" />
                <div><p className="text-sm text-foreground">{t("client.paymentPending", { amount: formatUSD(req.amount) })}</p><p className="text-xs text-muted-foreground">{t("client.completeToBegin")}</p></div>
              </div>
              <Button onClick={pay} disabled={paying} className="rounded-sm bg-amber-500 text-white hover:bg-amber-600" data-testid="detail-pay-btn">
                {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : t("client.payAmount", { amount: formatUSD(req.amount) })}
              </Button>
            </div>
          )}

          <div className="mt-8 rounded-2xl border border-border bg-card p-6 sm:p-8 card-soft">
            <h2 className="font-heading text-xl tracking-tight mb-6 text-foreground">{t("client.status")}</h2>
            <StatusTracker stage={req.stage} orientation="vertical" />
            {(req.next_action || req.est_completion || (req.customer_notes || []).length > 0 || (req.status_history || []).length > 0) && (
              <div className="mt-5 border-t border-border pt-4 space-y-3" data-testid="work-updates">
                {req.next_action && (
                  <div className="rounded-sm border border-orange-200 bg-orange-50 px-3 py-2">
                    <p className="text-[11px] uppercase tracking-widest text-orange-700">{t("portal.nextAction")}</p>
                    <p className="text-sm text-foreground mt-0.5">{req.next_action}</p>
                  </div>
                )}
                {req.est_completion && <p className="text-xs text-muted-foreground">{t("client.estCompletion")} <span className="text-foreground">{req.est_completion}</span> <span className="italic">{t("client.estimateOnly")}</span></p>}
                {(req.customer_notes || []).length > 0 && (
                  <div className="space-y-2">
                    <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{t("client.teamUpdates")}</p>
                    {req.customer_notes.slice().reverse().map((n, i) => (
                      <div key={i} className="rounded-sm border border-border bg-secondary/40 px-3 py-2" data-testid={`customer-note-${i}`}>
                        <p className="text-sm text-foreground whitespace-pre-wrap">{n.text}</p>
                        <p className="text-[11px] text-muted-foreground mt-1">{new Date(n.at).toLocaleDateString()}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* DOCUMENTS — tracking + upload */}
          <div className="mt-6 rounded-2xl border border-border bg-card p-6 sm:p-8 card-soft" data-testid="detail-documents">
            <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
              <h2 className="flex items-center gap-2 font-heading text-xl tracking-tight text-foreground"><FileText className="h-5 w-5 text-orange-700" /> {t("client.documents")}</h2>
              <Button size="sm" variant="outline" disabled={uploading === "new"} onClick={() => pickFile(null)} className="rounded-sm border-slate-300 bg-card hover:bg-secondary h-9 text-sm" data-testid="doc-upload-new">
                {uploading === "new" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1.5" /> {t("client.uploadDoc")}</>}
              </Button>
            </div>

            {reqDocs.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("client.noDocs")}</p>
            ) : (
              <div className="space-y-3">
                {reqDocs.map((d) => {
                  const st = DOC_STATUS[d.status] || DOC_STATUS.required;
                  const busy = uploading === d.id;
                  return (
                    <div key={d.id} className="rounded-lg border border-border bg-secondary/30 p-4" data-testid={`doc-row-${d.id}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-foreground">{d.name}</span>
                            <Badge className={`rounded-sm border text-[10px] uppercase tracking-wider ${st.c}`} data-testid={`doc-status-${d.id}`}>{t(st.t)}</Badge>
                          </div>
                          {d.filename && (
                            <button onClick={() => setPreview(d)} className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-orange-700" data-testid={`doc-view-${d.id}`}>
                              <FileText className="h-3.5 w-3.5" /> {d.filename}
                            </button>
                          )}
                          {d.uploaded_at && <p className="text-[11px] text-muted-foreground mt-0.5">{t("client.uploadedOn", { date: new Date(d.uploaded_at).toLocaleDateString() })}</p>}
                          {d.note && <p className="mt-2 text-xs text-orange-800 bg-orange-50 border border-orange-100 rounded-sm px-2.5 py-1.5">{d.note}</p>}
                        </div>
                        <Button size="sm" variant={needsAction(d.status) ? "default" : "outline"} disabled={busy}
                          onClick={() => pickFile(d.id)}
                          className={`rounded-sm h-9 text-sm shrink-0 ${needsAction(d.status) ? "bg-orange-500 text-white hover:bg-orange-600" : "border-slate-300 bg-card hover:bg-secondary"}`}
                          data-testid={`doc-upload-${d.id}`}>
                          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{d.filename ? <><RefreshCw className="h-4 w-4 mr-1.5" /> {t("client.replace")}</> : <><Upload className="h-4 w-4 mr-1.5" /> {t("client.upload")}</>}</>}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-6">
            {req.line_items?.length > 0 && (
              <div className="md:col-span-2 rounded-2xl border border-border bg-card p-6 card-soft" data-testid="detail-order-summary">
                <h3 className="font-heading text-lg tracking-tight mb-4 text-foreground">{t("client.orderSummary")}</h3>
                <div className="space-y-2 text-sm">
                  {req.line_items.map((it, i) => (
                    <div key={i} className="flex items-center justify-between gap-3"><span className="text-muted-foreground">{it.label}</span><span className={`font-mono ${it.amount < 0 ? "text-green-700" : "text-foreground"}`}>{formatUSD(it.amount)}</span></div>
                  ))}
                  <div className="pt-3 mt-1 border-t border-border flex items-center justify-between"><span className="text-muted-foreground">{t("client.total")}</span><span className="font-heading text-xl text-foreground">{formatUSD(req.amount)}</span></div>
                </div>
              </div>
            )}
            <div className="rounded-2xl border border-border bg-card p-6 card-soft">
              <h3 className="flex items-center gap-2 font-heading text-lg tracking-tight mb-4 text-foreground"><Building2 className="h-4 w-4 text-orange-700" /> {t("client.company")}</h3>
              <dl className="space-y-2.5 text-sm">
                <Row label={t("client.companyName")} value={req.company_name} />
                <Row label={t("client.entityType")} value={req.entity_type || "—"} />
                <Row label={t("client.jurisdiction")} value={req.jurisdiction} />
                <Row label={t("client.contact")} value={req.contact_name} />
                <Row label={t("client.email")} value={req.contact_email} />
              </dl>
              {req.business_activity && <p className="mt-4 text-sm text-muted-foreground leading-relaxed border-t border-border pt-4">{req.business_activity}</p>}
            </div>

            {req.directors?.length > 0 && (
              <div className="rounded-2xl border border-border bg-card p-6 card-soft">
                <h3 className="font-heading text-lg tracking-tight mb-4 text-foreground">{t("client.directors")}</h3>
                <ul className="space-y-1.5 text-sm">
                  {req.directors.filter((d) => d.full_name).map((d, i) => (
                    <li key={i} className="flex justify-between"><span className="text-foreground">{d.full_name}</span><span className="text-muted-foreground">{d.ownership || d.nationality || ""}</span></li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </motion.div>
        <DocPreviewModal doc={preview} prefix="/files" onClose={() => setPreview(null)} />
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground text-right truncate">{value}</dd>
    </div>
  );
}
